(ns rothko.collection-components.table.component
  (:require [reagent.core :as r]
            [rothko.collection-components.core :as collection]
            [rothko.collection-components.filters.component :as filters]
            [rothko.form2.select.single-select.component :as select]
            [rothko.icon.component :as icon]
            [rothko.button.component :refer [button]]
            [rothko.dom.url :as url]
            [rothko.form2.component :as form]))

(defn update-url
  [new-state]
  (url/merge-params-into-query-string new-state))

(defn column-name-on-click
  [{collection-update-fn ::collection/update} !state
   {:keys [::collection/db-column-name ::collection/id ::ordered?]
    :or {ordered? false}
    :as column}
   _]
  (let [field (or db-column-name id)]
    (when (and field ordered?)
      (let [old-state @!state
            state (swap! !state update :ordering
                         (fn [{:keys [order] :as ordering}]
                           {:field field
                            :order (if (= :desc order) :asc :desc)}))]
        (when (not= old-state state)
          (do (update-url state)
              (collection-update-fn state)))))))

(defn column-ordering-icon
  [_props !state {:keys [::collection/id ::ordered?] :or {ordered? false} :as _column}]
  (let [ordering (when (= id (-> @!state :ordering :field))
                   (-> @!state :ordering :order))]
    (case ordering
      :asc [icon/icon
            {:icon :angle-up
             :size 25}]
      :desc [icon/icon
             {:icon :angle-down
              :size 25}]
      nil (when ordered?
            [icon/icon
             {:icon :angle-up-and-down
              :size 25}]))))

(defn field-filter
  [_ _ _]
  (let [!filter-state (r/atom {:open? false})
        filter-icon-on-click #(swap! !filter-state update :open? not)]
    (fn [{:keys [appearance] :as props}
         column
         field-props]
      (let [{:keys [::form/component] :as filter-field}
            (get field-props (-> column ::filters/filter ::form/id))]
        (when filter-field
          [:div.table__field-filter
           {:class appearance}
           [icon/icon
            {:class (conj appearance :table__field-filter-icon)
             :icon :filter
             :on-click filter-icon-on-click}]
           (when (:open? @!filter-state)
             [component filter-field])])))))

(defn column-name
  [props !state column field-props]
  (let [on-label-click (partial column-name-on-click props !state column)]
    (fn [{:keys [appearance]} _ {:keys [::collection/label ::filters/filter]} _]
      [:th.table__column-name-container
       {:class appearance}
       [:div.table__column-name
        {:class appearance}
        [:span {:on-click on-label-click} label]
        [column-ordering-icon props !state column]
        [field-filter props column field-props]]])))

(defn table-header
  [{:keys [appearance]
    columns ::collection/fields
    collection-update-fn ::collection/update
    :as props}
   !state]
  (let [props (assoc props ::filters/on-change #(do (update-url %) (collection-update-fn %)))]
    [filters/form props !state
     (fn [_ field-props]
       [:thead.table__header
        {:class appearance}
        (into [:tr]
              (map (fn [column]
                     [column-name props !state column field-props])
                   columns))])]))

(defn field
  [{:keys [appearance] :as props}
   {:keys [::collection/id ::formatter]}
   row-data]
  [:td.table__field
   {:class appearance}
   (if formatter
     [formatter props row-data]
     (get row-data id))])

(defn row
  [{:keys [appearance ::row-on-click]
    columns ::collection/fields
    :or {row-on-click (constantly :no-op)}
    :as props}
   {:keys [id selected?] :as row-data}]
  (into [:tr.table__row
         {:class (cond-> appearance
                   selected? (conj :selected))
          :on-click #(row-on-click row-data)}]
        (for [column columns]
          [field props column row-data])))

(defn table-rows
  [{:keys [appearance]
    rows ::collection/items
    columns ::collection/fields
    :as props}]
  (if-not (empty? rows)
    (into [:tbody.table__body {:class appearance}]
          (map (fn [row-data]
                 [row props row-data])
               rows))
    [:tbody.table__body {:class appearance}
     [:tr.table__row
      {:class appearance}
      [:td.table__field
       {:class appearance
        :col-span (count columns)}
       [:em "No data"]]]]))

(defn pagination-page
  [{collection-update-fn ::collection/update} !state page-number]
  (let [{{:keys [page]} :pagination} @!state]
    [:li.table__pagination-page-number
     (if (not= page page-number)
       [:a
        {:href ""
         :on-click #(let [state (swap! !state assoc-in [:pagination :page] page-number)]
                      (update-url state)
                      (collection-update-fn state))}
        page-number]
       [:span page-number])]))

(defn page-size-field
  [{collection-update-fn ::collection/update
    pagination ::collection/pagination} !state]
  [form/form
   {::form/on-field-change #(when-let [page-size (:rothko.form2.component/value %)]
                              (let [{{old-page-size :size} :pagination} @!state]
                                (when (not= old-page-size page-size)
                                  (let [[_ new-state] (swap-vals! !state assoc :pagination {:page 1 :size page-size})]
                                    (update-url new-state)
                                    (collection-update-fn new-state)))))}
   {:pagination-props
    (merge pagination
      {::form/initial-value (get-in @!state [:pagination :size])})}
   (fn [{:keys [::form/submit!]} {:keys [pagination-props]}]
     [:div.table__pagination-size [:div.pagination-size-label "Size: "]
      [:div [select/field pagination-props]]])])

(defn page-links
  [{:keys [appearance ::collection/total] :as props}
   !state]
  (let [{{:keys [page size]} :pagination} @!state
        max-pages 9
        total-pages (js/Math.ceil (/ total size))
        before-current (js/Math.floor (/ max-pages 2))
        after-current (dec (js/Math.ceil (/ max-pages 2)))
        start-page (cond
                     (<= total-pages max-pages) 1
                     (< page before-current) 1
                     (>= (+ page after-current) total-pages) (inc (- total-pages max-pages))
                     :else (- page before-current))
        end-page (cond
                   (<= total-pages max-pages) total-pages
                   (< page before-current) max-pages
                   (>= (+ page after-current) total-pages) total-pages
                   :else (+ page after-current))
        start-index (* size (dec page))
        end-index (min (dec (+ start-index size))
                       (dec total))
        page-range (map #(+ start-page %)
                        (range (- (inc end-page) start-page)))]
    [:div.table__pagination-page
     {:class appearance}
     "Page: "
     (cond-> [:ul.table__pagination-pages]
       (< 1 start-page) (conj [pagination-page props !state 1]
                              [:li "..."])
       true (into (map (fn [page-number]
                         [pagination-page props !state page-number])
                       page-range))
       (> total-pages end-page) (conj [:li "..."]
                                      [pagination-page props !state total-pages]))]))

(defn items-total
  [{:keys [appearance ::collection/total] :as props}]
  (when total
    [:span.table__pagination-total
     {:class appearance}
     (str "Total: " total)]))

(defn table-footer
  [{:keys [appearance ::collection/total ::footer]
    columns ::collection/fields
    :as props}
   !state]
  (when footer
    [:tfoot.table__footer
     {:class appearance}
     [:tr
      [:td
       {:col-span (count columns)}
       [:div.table__footer-inner
        [items-total props]
        [page-links props !state]
        [page-size-field props !state]]]]]))

(defn component*
  [_]
  (let [url-params (url/extract-params-from-query-string)
        !state (r/atom (cond-> url-params
                         (not (:pagination url-params)) (assoc :pagination {:page 1 :size 50})))]
    (fn [{:keys [appearance] :as props}]
      [:table {:class appearance}
       [table-header props !state]
       [table-rows props]
       [table-footer props !state]])))

(defn component
  [props]
  ^{:key (select-keys props [::collection/fields ::collection/update ::collection/total])}
  [component* props])
